import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { AppUser } from '../../../app/models/appUser';
import { debounceTime, distinctUntilChanged, first, map } from 'rxjs/operators';
import { AuthenticationService } from './authentication.service';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { toClientIdSegment } from '../safe-path-segments';

@Injectable({ providedIn: 'root' })
export class UserService {
    private readonly userApiUrl = environment.apiRoot + 'users/';
    private readonly clientApiUrl = environment.apiRoot + 'clients/';

    constructor(private readonly httpClient: HttpClient, private readonly authService: AuthenticationService) { }





    async updateUser(userData) {
        try {

            const data = userData.raw ? userData.raw : userData;
            const CreateData = {

                FirstName: userData.raw.FirstName,
                LastName: userData.raw.LastName,
                ClientId: userData.raw.ClientID,
                Email: userData.raw.EmailId,
                Role: userData.raw.RoleId,
                LoginType: data.LoginType,
                Avatar: '',
                // NetworkID: userData.NetworkID,
                IsAvatarUpdated: false,
                IsActive: userData.raw.IsActive,
                startDate: userData.startDate,
                endDate: userData.endDate,
                // Internal users are excluded from the client's license count by the API.
                IsInternalUser: !!data.IsInternalUser,
                // Only used to deliver the temporary password - login stays on Email.
                AlternateEmail: data.IsInternalUser ? (data.AlternateEmail ?? '').trim() : ''

            };

            const headerOptions = new HttpHeaders();
            headerOptions.append("Content-Type", "application/json");
            let apiurl = environment.apiRoot;

            if (data.Id == 0) {
                return await firstValueFrom(this.httpClient
                    .post<any>(this.userApiUrl + "create", CreateData));
            } else {
                const UpdateData: any = {
                    Id: data.Id,
                    FirstName: data.FirstName,
                    LastName: data.LastName,
                    ClientId: data.ClientID,
                    Email: data.EmailId,
                    Role: data.RoleId,
                    LoginType: data.LoginType,
                    Avatar: '',
                    // NetworkID: userData.NetworkID,
                    startDate: userData.startDate,
                    endDate: userData.endDate,
                    IsAvatarUpdated: false,
                    IsActive: data.IsActive

                };

                // AlternateEmail is create-only - the API neither stores nor updates it.
                // IsInternalUser is sent only when the caller could state it
                // authoritatively; omitting it leaves the stored value untouched and
                // avoids tripping the Super Admin guard.
                const isInternalUser = userData.IsInternalUser;
                if (isInternalUser !== undefined && isInternalUser !== null) {
                    UpdateData.IsInternalUser = !!isInternalUser;
                }


                return await firstValueFrom(this.httpClient
                    .put<any>(this.userApiUrl + "update", UpdateData, { headers: headerOptions }));
            }

        } catch (err: any) {
            console.error(err);
            // Hand the API's own body back to the caller - e.g. the 400 raised when a
            // non-SuperAdmin flips IsInternalUser - so it can show that message rather
            // than a bare failure.
            return err?.error ?? { message: err?.message };
        }
    }


    async getRoles() {
        try {
            return await lastValueFrom(this.httpClient.get(this.userApiUrl + 'picklist/roles')
                .pipe(
                    map((res: any) => { return res })));
        } catch (err) {
            console.error(err);
            return false;
        }
    }
    async getUsers(searchTerm = "", ClientId = "", PageNum = 1, PageSize = 50, SortColumn = "", isAsc = true) {
        try {
            const searchData = {
                Keyword: searchTerm,
                clientId: ClientId,
                pageNum: PageNum,
                pageSize: PageSize,
                sortColumn: SortColumn,
                IsAsc:isAsc
            };
            const headerOptions = new HttpHeaders();
            headerOptions.append("Content-Type", "application/json");
            let apiurl = environment.apiRoot;
            return await firstValueFrom(
                this.httpClient.post<any>(this.userApiUrl + "search", searchData)
            )

        } catch (err) {
            console.error("Error :", err);
            return [];
        }
    }
    async getUserws(): Promise<AppUser[]> {
        try {
            return await lastValueFrom(this.httpClient
                .get(this.userApiUrl + 'search')
                .pipe(
                    map((res: any) => {
                        res = JSON.parse(res);
                        res.forEach((val, i) => {
                            val.userId = val.loginId;
                            val.employeeName = val.firstName + ' ' + val.lastName;
                        });
                        return res;
                    })
                ));
        }
        catch (err) {
            console.error(err);
            return [];
        }
    }
    async getClientUsers(clientId: any, AllEmployeeRes: any): Promise<AppUser[]> {
        try {
            return await lastValueFrom(this.httpClient
                .get(this.userApiUrl + "getClientUser/" + clientId)
                .pipe(
                    map((res: any) => {
                        let res2: any = res;
                        res.forEach((val, i) => {
                            let data = AllEmployeeRes.filter(a => a.loginId.toUpperCase().includes(val.employeeId.toUpperCase()));
                            res2[i] = data[0];
                        });
                        return res2;
                    })
                ));
        } catch (error) {
            console.error('Error ', error);
            return [];
        }
    }

    async deleteUser(userId: any) {

        try {


            const headerOptions = new HttpHeaders();
            headerOptions.append('Content-Type', 'application/json');
            return await firstValueFrom(this.httpClient.delete<any>(this.userApiUrl + "delete/" + userId, { headers: headerOptions }));

        } catch (e: any) {
            console.error("An error occurred: ", e);
            throw e;
        }
    }
    async getUserTeamMapping(userId: number) {
        return await lastValueFrom(this.httpClient
            .get(environment.apiRoot + 'api/UserTeamMapping/' + userId));
    }
    async getUserClientName(userId: any) {
        const data = await lastValueFrom(this.httpClient.get(environment.apiRoot + 'api/User/UserClientName/' + userId));
        return data;
    }
    // The id segment is rebuilt from the parsed integer rather than copied from the
    // argument - see shared/safe-path-segments.ts (Veracode CWE-601). An invalid id
    // throws before any request is made; the callers already catch and toast.
    async getUniqueUsersCount(clientId: string): Promise<number> {
        return await firstValueFrom(
            this.httpClient.get<number>(
                this.clientApiUrl + toClientIdSegment(clientId) + '/unique-users/count'
            )
        );
    }
    async getActiveUsersCount(clientId: string, days: number): Promise<number> {
        const params = new HttpParams().set('days', String(days));
        return await firstValueFrom(
            this.httpClient.get<number>(
                this.clientApiUrl + toClientIdSegment(clientId) + '/active-users/count',
                { params }
            )
        );
    }
    isTeamCodeAvailable(teamCode, teamId = '') {
        let paramsData = new HttpParams();
        paramsData = paramsData.append('Code', teamCode);
        paramsData = paramsData.append('Id', teamId);
        return this.httpClient
            .get(this.userApiUrl + 'GetByCode', { params: paramsData })
            .pipe(
                debounceTime(500),
                distinctUntilChanged(),
                map((value: any) => value?.length ? true : null)
            )
            .pipe(first());
    }

    async isUserExsits(formData) {
        try {
            const headerOptions = new HttpHeaders();
            headerOptions.append('Content-Type', 'multipart/form-data');
            headerOptions.append('Accept', 'application/json');
            let url = environment.apiRoot + 'api/User/IsUserExsits';
            return await lastValueFrom(this.httpClient.post(url, formData, { headers: headerOptions }));
        } catch (err) {
            console.error(err);
            return false;
        }
    }



    async getUserByNTLogin(formData) {
        try {
            const headerOptions = new HttpHeaders();
            headerOptions.append('Content-Type', 'multipart/form-data');
            headerOptions.append('Accept', 'application/json');
            let url = environment.apiRoot + 'api/User/UserDetail';
            return await lastValueFrom(this.httpClient
                .post(url, formData, { headers: headerOptions })
                .pipe(
                    map(d => {
                        if (typeof d === 'string') {
                            d = JSON.parse(d);
                        }
                        return d;
                    })
                ));
        } catch (err) {
            console.error(err);
            return false;
        }
    }
}
